import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/primitives/dialog";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Switch } from "@/ui/components/primitives/switch";
import { Textarea } from "@/ui/components/primitives/textarea";
import {
  updatePSPConfiguration,
  type PSPConfiguration,
  type UpdatePSPConfigRequest,
} from "@/services/api/services/psp-management";
import { showErrorToast, showSuccessToast } from "@/services/toast-helper";
import { Loader2 } from "lucide-react";
import { cn } from "@/ui/components/primitives/utils";

interface EditPSPConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PSPConfiguration | null;
  onUpdateComplete?: () => void;
}

export function EditPSPConfigModal({
  open,
  onOpenChange,
  config,
  onUpdateComplete,
}: EditPSPConfigModalProps) {
  const [loading, setLoading] = useState(false);

  // Form state
  const [isEnabled, setIsEnabled] = useState(true);
  const [isSandbox, setIsSandbox] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState(1);

  // URLs
  const [webhookUrl, setWebhookUrl] = useState("");
  const [returnUrl, setReturnUrl] = useState("");

  // Methods and currencies
  const [paymentMethods, setPaymentMethods] = useState("");
  const [currencies, setCurrencies] = useState("");

  // Limits
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");

  // Fees
  const [feePercentage, setFeePercentage] = useState("");
  const [feeFixed, setFeeFixed] = useState("");
  const [feeCurrency, setFeeCurrency] = useState("INR");

  // Features
  const [featuresJson, setFeaturesJson] = useState("{}");

  // Credentials
  const [credentialsJson, setCredentialsJson] = useState("{}");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load config data when modal opens or config changes
  useEffect(() => {
    if (config && open) {
      setIsEnabled(config.isEnabled);
      setIsSandbox(config.isSandbox);
      setIsActive(config.isActive);
      setPriority(config.priority);
      setWebhookUrl(config.webhookUrl || "");
      setReturnUrl(config.returnUrl || "");
      setPaymentMethods(config.paymentMethods.join(", "));
      setCurrencies(config.currencies.join(", "));
      setMinAmount(String(config.limits.minAmount));
      setMaxAmount(String(config.limits.maxAmount));
      setFeePercentage(String(config.fees.percentage));
      setFeeFixed(String(config.fees.fixed));
      setFeeCurrency(config.fees.currency);
      setFeaturesJson(JSON.stringify(config.features, null, 2));
      setCredentialsJson(JSON.stringify(config.credentials, null, 2));
    }
  }, [config, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!paymentMethods.trim()) {
      newErrors.paymentMethods = "At least one payment method is required";
    }

    if (!currencies.trim()) {
      newErrors.currencies = "At least one currency is required";
    }

    const min = parseFloat(minAmount);
    const max = parseFloat(maxAmount);
    if (isNaN(min) || min < 0) {
      newErrors.minAmount = "Invalid minimum amount";
    }
    if (isNaN(max) || max < 0) {
      newErrors.maxAmount = "Invalid maximum amount";
    }
    if (!isNaN(min) && !isNaN(max) && max < min) {
      newErrors.maxAmount = "Maximum amount must be greater than minimum";
    }

    const percentage = parseFloat(feePercentage);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      newErrors.feePercentage = "Fee percentage must be between 0 and 100";
    }

    const fixed = parseFloat(feeFixed);
    if (isNaN(fixed) || fixed < 0) {
      newErrors.feeFixed = "Fixed fee must be 0 or greater";
    }

    if (featuresJson.trim()) {
      try {
        JSON.parse(featuresJson);
      } catch {
        newErrors.featuresJson = "Invalid JSON format";
      }
    }

    if (credentialsJson.trim()) {
      try {
        JSON.parse(credentialsJson);
      } catch {
        newErrors.credentialsJson = "Invalid JSON format";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!config) return;

    if (!validateForm()) {
      showErrorToast("Please fix validation errors");
      return;
    }

    setLoading(true);
    try {
      const credentials = credentialsJson.trim()
        ? JSON.parse(credentialsJson)
        : config.credentials;

      const features = featuresJson.trim()
        ? JSON.parse(featuresJson)
        : config.features;

      const data: UpdatePSPConfigRequest = {
        isEnabled,
        isSandbox,
        isActive,
        credentials,
        webhookUrl: webhookUrl || undefined,
        returnUrl: returnUrl || undefined,
        features,
        paymentMethods: paymentMethods.split(",").map((m) => m.trim()),
        currencies: currencies.split(",").map((c) => c.trim()),
        limits: {
          minAmount: parseFloat(minAmount),
          maxAmount: parseFloat(maxAmount),
        },
        fees: {
          percentage: parseFloat(feePercentage),
          fixed: parseFloat(feeFixed),
          currency: feeCurrency,
        },
        priority,
      };

      await updatePSPConfiguration(config.id, data);
      showSuccessToast("PSP configuration updated successfully");
      onOpenChange(false);
      onUpdateComplete?.();
    } catch (error: any) {
      showErrorToast(
        error?.detail?.error?.message ||
          error?.message ||
          "Failed to update PSP configuration"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!config) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit PSP Configuration</DialogTitle>
          <DialogDescription>
            Update PSP configuration for {config.merchantId} - {config.provider}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status Toggles */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Status</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isEnabled"
                  checked={isEnabled}
                  onCheckedChange={setIsEnabled}
                />
                <Label htmlFor="isEnabled">Enabled</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isSandbox"
                  checked={isSandbox}
                  onCheckedChange={setIsSandbox}
                />
                <Label htmlFor="isSandbox">Sandbox Mode</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority (1-100)</Label>
              <Input
                id="priority"
                type="number"
                min="1"
                max="100"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          {/* Credentials (JSON Editor) */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Credentials (JSON)</h3>

            <div className="space-y-2">
              <Label htmlFor="credentialsJson">Credentials Configuration</Label>
              <Textarea
                id="credentialsJson"
                value={credentialsJson}
                onChange={(e) => setCredentialsJson(e.target.value)}
                placeholder='{"apiKey": "xxx", "apiSecret": "yyy"}'
                rows={5}
                className={cn("font-mono text-xs", errors.credentialsJson && "border-destructive")}
              />
              {errors.credentialsJson && (
                <p className="text-xs text-destructive">{errors.credentialsJson}</p>
              )}
            </div>
          </div>

          {/* URLs */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Webhook & Return URLs</h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="webhookUrl">Webhook URL</Label>
                <Input
                  id="webhookUrl"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://api.paygate.sbs/api/v1/webhooks/provider"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="returnUrl">Return URL</Label>
                <Input
                  id="returnUrl"
                  value={returnUrl}
                  onChange={(e) => setReturnUrl(e.target.value)}
                  placeholder="https://merchant.paygate.sbs/payment/success"
                />
              </div>
            </div>
          </div>

          {/* Payment Methods & Currencies */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Payment Methods & Currencies</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentMethods">Payment Methods (comma-separated) *</Label>
                <Input
                  id="paymentMethods"
                  value={paymentMethods}
                  onChange={(e) => setPaymentMethods(e.target.value)}
                  placeholder="UPI,CARD,NET_BANKING"
                  className={cn(errors.paymentMethods && "border-destructive")}
                />
                {errors.paymentMethods && (
                  <p className="text-xs text-destructive">{errors.paymentMethods}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="currencies">Currencies (comma-separated) *</Label>
                <Input
                  id="currencies"
                  value={currencies}
                  onChange={(e) => setCurrencies(e.target.value)}
                  placeholder="INR,USD"
                  className={cn(errors.currencies && "border-destructive")}
                />
                {errors.currencies && (
                  <p className="text-xs text-destructive">{errors.currencies}</p>
                )}
              </div>
            </div>
          </div>

          {/* Limits */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Transaction Limits</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minAmount">Minimum Amount *</Label>
                <Input
                  id="minAmount"
                  type="number"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className={cn(errors.minAmount && "border-destructive")}
                />
                {errors.minAmount && (
                  <p className="text-xs text-destructive">{errors.minAmount}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxAmount">Maximum Amount *</Label>
                <Input
                  id="maxAmount"
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className={cn(errors.maxAmount && "border-destructive")}
                />
                {errors.maxAmount && (
                  <p className="text-xs text-destructive">{errors.maxAmount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Fees */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Fees</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="feePercentage">Percentage Fee (%) *</Label>
                <Input
                  id="feePercentage"
                  type="number"
                  step="0.1"
                  value={feePercentage}
                  onChange={(e) => setFeePercentage(e.target.value)}
                  className={cn(errors.feePercentage && "border-destructive")}
                />
                {errors.feePercentage && (
                  <p className="text-xs text-destructive">{errors.feePercentage}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="feeFixed">Fixed Fee *</Label>
                <Input
                  id="feeFixed"
                  type="number"
                  value={feeFixed}
                  onChange={(e) => setFeeFixed(e.target.value)}
                  className={cn(errors.feeFixed && "border-destructive")}
                />
                {errors.feeFixed && (
                  <p className="text-xs text-destructive">{errors.feeFixed}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="feeCurrency">Fee Currency *</Label>
                <Input
                  id="feeCurrency"
                  value={feeCurrency}
                  onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())}
                  maxLength={3}
                />
              </div>
            </div>
          </div>

          {/* Features (JSON) */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Features (JSON)</h3>

            <div className="space-y-2">
              <Label htmlFor="featuresJson">Features Configuration</Label>
              <Textarea
                id="featuresJson"
                value={featuresJson}
                onChange={(e) => setFeaturesJson(e.target.value)}
                placeholder='{"refunds": true, "recurring": false}'
                rows={5}
                className={cn("font-mono text-xs", errors.featuresJson && "border-destructive")}
              />
              {errors.featuresJson && (
                <p className="text-xs text-destructive">{errors.featuresJson}</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Created: {new Date(config.createdAt).toLocaleString()}</p>
            <p>Updated: {new Date(config.updatedAt).toLocaleString()}</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdate} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
